import ApiService from "./ApiService";
import { resolveApiFetchUrl } from "./ApiBaseUrl";
import Logger from "./Logger";

const CLIENT_SESSION_KEY = "puzzle.playClientSessionId";
const PLAY_RECORD_BASE = "/home/play-record";

/** Stable anonymous id for play analytics (survives refresh; not tied to login). */
export function getPlayClientSessionId() {
    try {
        let id = localStorage.getItem(CLIENT_SESSION_KEY);
        if (!id) {
            id =
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 11)}`;
            localStorage.setItem(CLIENT_SESSION_KEY, id);
        }
        return id;
    } catch {
        return null;
    }
}

function newVisitId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 11)}`;
}

export { newVisitId };

function buildPayload({ visitId, puzzleType, puzzleId, pagePath }) {
    return {
        visitId,
        puzzleType,
        puzzleId,
        clientSessionId: getPlayClientSessionId(),
        pagePath: pagePath || (typeof window !== "undefined" ? window.location.pathname : null),
    };
}

function playRecordUrl(type) {
    const segment = type === "LEAVE" ? "leave" : "enter";
    return `${PLAY_RECORD_BASE}/${segment}`;
}

/** Best-effort leave when the tab closes. */
function postLeaveBeacon(body) {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) {
        return false;
    }
    try {
        const url = resolveApiFetchUrl(playRecordUrl("LEAVE"));
        const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
        return navigator.sendBeacon(url, blob);
    } catch {
        return false;
    }
}

const PlayRecordService = {
    newVisitId,

    /**
     * Fire-and-forget play record event. No login required; ip_addr is set server-side.
     * @param {{ eventType?: 'ENTER'|'LEAVE', visitId?: string|null, puzzleType: string, puzzleId: string, pagePath?: string|null, useBeacon?: boolean }} params
     */
    async recordEvent({ eventType = "ENTER", visitId, puzzleType, puzzleId, pagePath, useBeacon = false }) {
        if (!puzzleType || !puzzleId) {
            return;
        }
        const body = buildPayload({
            visitId: visitId || newVisitId(),
            puzzleType,
            puzzleId,
            pagePath,
        });

        if (useBeacon && eventType === "LEAVE" && postLeaveBeacon(body)) {
            return;
        }

        try {
            const response = await ApiService.postDataOptionalAuth(playRecordUrl(eventType), body);
            if (response?.code === "FAILURE") {
                Logger.debug("PlayRecordService.recordEvent rejected:", response.message);
            }
        } catch (err) {
            Logger.debug("PlayRecordService.recordEvent failed:", err);
        }
    },

    recordEnter(params) {
        return this.recordEvent({ ...params, eventType: "ENTER" });
    },

    recordLeave(params) {
        return this.recordEvent({ ...params, eventType: "LEAVE" });
    },
};

export default PlayRecordService;
